import styled from "styled-components";
import {brand, overlay, radii} from "theme/tokens";

/**
 * Externalized styles for every control under `src/sponsor/options/`
 * (kitecorp/transferify-ui#1868).
 *
 * ONE styles module for the folder rather than one per component, and the reason is that these controls
 * are a SET: a direction, a placement grid, two colour rows and three file pickers that have to read as the same
 * kind of thing stacked in one panel. Split six ways, the field label, the hint and the error line would
 * be six near-copies, and the first restyle would move five of them. The same arrangement
 * `TemplatePreviewLauncher.styles.ts` already uses for the control the picker mounts beside its cards.
 *
 * Every colour is a `theme.tokens` read, so the panel follows light and dark with no second stylesheet.
 */

/**
 * One labelled control.
 *
 * A `<fieldset>` because most of these ARE groups of radios, and a fieldset with a legend is what names
 * such a group for a screen reader without an `aria-labelledby` that has to be kept pointing at the
 * right id. The browser's own border and padding are reset: the panel already draws the box.
 */
export const OptionField = styled.fieldset`
	margin: 0;
	padding: 0;
	border: 0;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const OptionLegend = styled.legend`
	padding: 0;
	font-size: 13.5px;
	font-weight: 700;
	letter-spacing: -0.005em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const OptionHint = styled.p`
	margin: 0;
	font-size: 12.5px;
	line-height: 1.4;
	/* ink2, not ink3: ink3 is the disabled tier and fails WCAG AA as readable text (ui#302). */
	color: ${({theme}) => theme.tokens.ink2};
`;

export const OptionError = styled.p`
	margin: 0;
	font-size: 12.5px;
	line-height: 1.4;
	color: ${({theme}) => theme.tokens.errorInk};
`;

/** The row a set of choice pills wraps inside. */
export const ChoiceRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`;

/**
 * One choice, as a `<label>` around a visually hidden radio — the picker's own arrangement, and for the
 * picker's own reason: arrow-key navigation, mutual exclusivity and the right announcement all come
 * from the native control, and a hand-rolled `role="radio"` reimplements two of the three at best.
 */
export const ChoicePill = styled.label<{$selected: boolean}>`
	display: inline-flex;
	align-items: center;
	/* 34px keeps a pill inside the 44px touch row once the 8px gap above and below is counted. */
	min-height: 34px;
	padding: 0 12px;
	border-radius: ${radii.chip};
	border: 1.5px solid ${({theme, $selected}) => ($selected ? theme.tokens.blue : theme.tokens.line)};
	background: ${({theme, $selected}) => ($selected ? theme.tokens.accentTint : theme.tokens.card)};
	color: ${({theme}) => theme.tokens.ink};
	font-size: 13px;
	font-weight: ${({$selected}) => ($selected ? 700 : 500)};
	cursor: pointer;
	transition: 0.14s;

	&:hover {
		border-color: ${({theme}) => theme.tokens.blue};
	}

	&:has(input:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * Removed from view but NOT from the accessibility tree or the tab order.
 * `display: none` would drop it from both, taking the keyboard behaviour this approach exists to get.
 */
export const HiddenControl = styled.input`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	border: 0;
	overflow: hidden;
	white-space: nowrap;
	clip-path: inset(50%);
`;

/**
 * The nine placements, as an actual 3x3 grid.
 *
 * A grid rather than nine pills in a row because the control IS a picture of the page: the cell a
 * sponsor clicks is where their words will sit, so the spatial arrangement is the affordance.
 *
 * A fixed maximum rather than an `em` measure. `em` was tried and is wrong here: this control mounts
 * inside two different surfaces whose inherited font sizes differ, so the same declaration drew a 192px
 * grid in one and a 300px one in the other, and the wide version pushed its whole column out. The
 * `min()` keeps it inside a narrow single-column layout at 390.
 */
export const PlacementGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 4px;
	width: min(100%, 210px);
	aspect-ratio: 16 / 10;
	padding: 4px;
	box-sizing: border-box;
	border-radius: ${radii.panel};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.field};
`;

/** One cell of the placement grid. The dot inside it is where the copy would start. */
export const PlacementCell = styled.label<{$selected: boolean}>`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: ${radii.chip};
	border: 1px solid ${({theme, $selected}) => ($selected ? theme.tokens.blue : "transparent")};
	background: ${({theme, $selected}) => ($selected ? theme.tokens.accentTint : "transparent")};
	cursor: pointer;
	transition: 0.14s;

	&:hover {
		background: ${({theme}) => theme.tokens.accentTint};
	}

	&:has(input:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 1px;
	}
`;

export const PlacementDot = styled.span<{$selected: boolean}>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${({theme, $selected}) => ($selected ? theme.tokens.blue : theme.tokens.ink3)};
`;

/** A quiet text control, for removing an uploaded asset. */
export const QuietButton = styled.button.attrs({type: "button"})`
	align-self: flex-start;
	padding: 6px 10px;
	min-height: 32px;
	border-radius: ${radii.chip};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 12.5px;
	cursor: pointer;
	transition: 0.14s;

	&:hover:not(:disabled) {
		border-color: ${({theme}) => theme.tokens.blue};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:disabled {
		color: ${({theme}) => theme.tokens.ink3};
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** The single-asset picker: a preview beside the control that replaces or removes it. */
export const AssetRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px;
`;

/**
 * The add/replace control. A `<label>` wrapping a hidden `<input type="file">`, which is the only way
 * to restyle a file picker at all: the native control's button is not styleable.
 */
export const AssetPickerLabel = styled.label<{$disabled: boolean}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 34px;
	padding: 0 12px;
	border-radius: ${radii.chip};
	border: 1.5px dashed ${({theme}) => theme.tokens.fieldLine};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 12.5px;
	font-weight: 700;
	cursor: ${({$disabled}) => ($disabled ? "not-allowed" : "pointer")};
	opacity: ${({$disabled}) => ($disabled ? 0.55 : 1)};
	transition: 0.16s;

	&:hover {
		border-color: ${({theme, $disabled}) => ($disabled ? theme.tokens.fieldLine : theme.tokens.blue)};
	}

	&:has(input:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * The uploaded mark or poster frame.
 *
 * `contain` rather than `cover`: a logo cropped to fill a box is a logo with its edges cut off, and
 * this preview exists to show the sponsor what they uploaded.
 */
export const AssetThumb = styled.img`
	width: 84px;
	height: 52px;
	object-fit: contain;
	border-radius: ${radii.panel};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.field};
`;

/** A video has no still to show here, so the picker names the file it is holding instead. */
export const AssetFilename = styled.span`
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
	overflow-wrap: anywhere;
`;

/* ------------------------------------- the focal-point marker ------------------------------------ */

/**
 * The picture the marker is dragged over (kitecorp/transferify-ui#2143).
 *
 * The `<img>` is the STAGE rather than something inside one, and that is what makes the maths honest:
 * a focal point is a fraction of the media's OWN box, so a pointer fraction taken against this
 * element's rect is that fraction directly, with no letterbox bands to subtract. Sizing this box
 * independently - a fixed 16:9 frame with `object-fit: contain` inside it - would put a band on every
 * file wider than 16:9 and make every drag a two-step conversion.
 *
 * `touch-action: none` is required rather than tidy: without it a drag on a phone scrolls the page and
 * the pointer events stop arriving mid-gesture.
 */
export const FocalStage = styled.div`
	position: relative;
	display: block;
	width: 100%;
	max-width: 320px;
	border-radius: ${radii.panel};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.field};
	overflow: hidden;
	touch-action: none;
	/* A fast drag would otherwise select the copy around the stage and leave it highlighted. */
	user-select: none;
	cursor: crosshair;
`;

/** The picked media at its own aspect ratio, which is the box a fraction is measured against. */
export const FocalMedia = styled.img`
	display: block;
	width: 100%;
	height: auto;
`;

/**
 * The marker itself: a ring rather than a dot, so what is under the point stays visible.
 *
 * A real `<button>`, because it is focusable, activatable and announced without reimplementing any of
 * the three. The two rings are one element's `box-shadow`, dark over light, so the marker reads on a
 * photograph of any brightness without a second element or a colour sampled at runtime.
 *
 * BOTH COLOURS ARE MODE-INDEPENDENT, and that is the correction rather than an oversight
 * (kitecorp/transferify-ui#2143's review). This ring sits on somebody's PHOTOGRAPH, not on a themed
 * surface, so it must not follow the theme: `theme.tokens.paper` is near-black in dark mode, which put
 * a black ring inside two black halos and made the marker vanish. `brand.white` and the shared
 * `overlay.scrim` are the two mode-independent constants this repo already keeps for exactly this
 * case, and the scrim replaces the raw rgba the review flagged.
 */
export const FocalMarker = styled.button`
	position: absolute;
	width: 26px;
	height: 26px;
	margin: -13px 0 0 -13px;
	padding: 0;
	border-radius: 50%;
	border: 2px solid ${brand.white};
	background: transparent;
	box-shadow: 0 0 0 2px ${overlay.scrim}, inset 0 0 0 2px ${overlay.scrim};
	cursor: grab;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 3px;
	}
`;

/** The point in words, under the picture: what a screen reader hears and a reviewer reads. */
export const FocalReadout = styled.p`
	margin: 0;
	font-size: 12.5px;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink2};
`;
